// Phase 6.6 D — Russian pluralisation helpers.
//
// Russian nouns have three grammatical-number forms (singular,
// "few" 2–4, "many" 5+) and a special override for the teens
// (11–14 always take the "many" form regardless of last digit).
// JavaScript's ``Intl.PluralRules`` *can* model this, but for a
// single noun the explicit decision table is shorter, dependency-
// free, and trivially testable.
//
// Pure functions only — no React, no DOM. Reusable across screens,
// future phases, and unit tests.

/** Pick a Russian plural form for the given count.
 *
 *  Phase 6.6 D introduces this for the «N задач» rendering in
 *  analytics summary cards (replacing the operator-unfriendly
 *  «n=N» notation), but the helper is intentionally generic so
 *  future call sites can plug their own three forms.
 *
 *  @param n     The count driving the form choice. Non-integer
 *               inputs (`1.5`, `NaN`) are normalised through
 *               `Math.abs(Math.trunc(n))` — fractional counts
 *               don't have a defined Russian form, and "1.5
 *               задача" / "1.5 задачи" both read fine, but the
 *               truncation matches what `Intl.NumberFormat` does
 *               under the hood.
 *  @param one   Form for "1, 21, 31, 101, …" — the bare singular.
 *  @param few   Form for "2–4, 22–24, 102–104, …".
 *  @param many  Form for "0, 5–20, 25–30, 100, 111, …".
 */
export function pluralize_ru(
  n: number,
  one: string,
  few: string,
  many: string,
): string {
  const abs = Math.abs(Math.trunc(n));
  const mod100 = abs % 100;
  // Teens (11–14) override the last-digit rule.
  if (mod100 >= 11 && mod100 <= 14) return many;
  const mod10 = abs % 10;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

/** Format a job count with the matching Russian noun form.
 *
 *  Examples:
 *    pluralize_jobs(1)   → "1 задача"
 *    pluralize_jobs(2)   → "2 задачи"
 *    pluralize_jobs(5)   → "5 задач"
 *    pluralize_jobs(11)  → "11 задач"   (teens override)
 *    pluralize_jobs(21)  → "21 задача"
 *    pluralize_jobs(22)  → "22 задачи"
 *    pluralize_jobs(101) → "101 задача"
 *    pluralize_jobs(111) → "111 задач"  (teens override)
 *
 *  Used by Phase 6.6 D in the analytics SummaryCards
 *  («ЛУЧШИЙ КОНФИГ … 6.3 мм · 4 задачи»). Kept here as a public
 *  helper so future analytics surfaces and series-card refinements
 *  can call it without copying the table. */
export function pluralize_jobs(n: number): string {
  return `${n} ${pluralize_ru(n, 'задача', 'задачи', 'задач')}`;
}
